import { useState } from "react";
import { useParams } from "react-router-dom";
import { ChangesSummaryPlanBT, GridElement, GridEditorError, GridEditorErrorType, SaveChangesResult, Connection } from "../types";
import { planBTAPI } from "../../../planBT/services/planAPI";
import { streetLightAPI } from "../../../planBT/services/streetLightAPI";
import { NodePlan } from "../../../planBT/types/node.types";
import { Pole, CentralPlant, Transformer } from "../../../planBT/types/planBT.types";
import { Meter } from "../../../meter/types/meter.types";
import { LinkPlan } from "../../../planBT/types/link.types";
import { StreetLightPatch } from "../../../planBT/types/streetLight.types";
import axios from "axios";

// Fonction utilitaire pour analyser les erreurs axios
const analyzeAxiosError = (error: any, operation: 'create' | 'update' | 'delete', elementId?: string): GridEditorError => {
    if (axios.isAxiosError(error)) {
        const status = error.response?.status;
        const message = error.response?.data?.message || error.message;
        
        switch (status) {
            case 400:
                return {
                    type: GridEditorErrorType.VALIDATION_ERROR,
                    message: `Erreur de validation: ${message}`,
                    details: error.response?.data,
                    elementId,
                    operation,
                    retryable: false
                };
            case 401:
            case 403:
                return {
                    type: GridEditorErrorType.PERMISSION_ERROR,
                    message: `Erreur d'autorisation: ${message}`,
                    details: error.response?.data,
                    elementId,
                    operation,
                    retryable: false
                };
            case 404:
                return {
                    type: GridEditorErrorType.NOT_FOUND_ERROR,
                    message: `Ressource non trouvée: ${message}`,
                    details: error.response?.data,
                    elementId,
                    operation,
                    retryable: false
                };
            case 409:
                return {
                    type: GridEditorErrorType.DUPLICATE_ERROR,
                    message: message || `Doublon détecté`,
                    details: error.response?.data,
                    elementId,
                    operation,
                    retryable: false
                };
            case 500:
            case 502:
            case 503:
            case 504:
                return {
                    type: GridEditorErrorType.SERVER_ERROR,
                    message: `Erreur serveur: ${message}`,
                    details: error.response?.data,
                    elementId,
                    operation,
                    retryable: true
                };
            default:
                return {
                    type: GridEditorErrorType.NETWORK_ERROR,
                    message: `Erreur réseau: ${message}`,
                    details: error.response?.data,
                    elementId,
                    operation,
                    retryable: true
                };
        }
    }
    
    return {
        type: GridEditorErrorType.UNKNOWN_ERROR,
        message: `Erreur inconnue: ${error.message}`,
        details: error,
        elementId,
        operation,
        retryable: false
    };
};

// Fonction utilitaire pour retry une opération
const retryOperation = async <T>(
    operation: () => Promise<T>,
    maxRetries: number = 3,
    delay: number = 1000
): Promise<T> => {
    let lastError: any;
    
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
        try {
            return await operation();
        } catch (error) {
            lastError = error;
            if (attempt < maxRetries) {
                await new Promise(resolve => setTimeout(resolve, delay * attempt));
            }
        }
    }
    
    throw lastError;
};

export const useGridEditorForPlanBT = (planBTId?: string) => {
    const [isLoading, setIsLoading] = useState(false);
    const [errors, setErrors] = useState<GridEditorError[]>([]);
    const [saveProgress, setSaveProgress] = useState<{
        current: number;
        total: number;
        currentStep: string;
        percentage: number;
    }>({
        current: 0,
        total: 0,
        currentStep: '',
        percentage: 0
    });

    let planBTIdFound = planBTId;
    const params = useParams();
    if (!planBTId) {
        planBTIdFound = params.planBTId;
    }

    const saveChanges = async (changes: ChangesSummaryPlanBT): Promise<SaveChangesResult> => {
        console.log('🚀 DÉBUT DE LA SAUVEGARDE - Résumé reçu:', {
            '🏗️ Éléments': {
                'À créer': changes.elements.create.length,
                'À modifier': changes.elements.update.length,
                'À supprimer': changes.elements.delete.length
            },
            '🔗 Connexions': {
                'À créer': changes.connections.create.length,
                'À modifier': changes.connections.update.length,
                'À supprimer': changes.connections.delete.length
            },
            '💡 Street Lights': {
                'À créer': changes.streetLights?.create?.length || 0,
                'À modifier': changes.streetLights?.update?.length || 0,
                'À supprimer': changes.streetLights?.delete?.length || 0
            }
        });
        
        if (!planBTIdFound) {
            const error: GridEditorError = {
                type: GridEditorErrorType.VALIDATION_ERROR,
                message: "Aucun ID de plan BT trouvé pour sauvegarder les modifications",
                retryable: false
            };
            setErrors([error]);
            return {
                success: false,
                errors: [error],
                message: "Impossible de sauvegarder sans ID de plan"
            };
        }

        setIsLoading(true);
        setErrors([]);
        
        // Calculer le nombre total d'opérations pour la progression
        const totalOperations = 
            (changes.elements?.create?.length || 0) +
            (changes.elements?.update?.length || 0) +
            (changes.elements?.delete?.length || 0) +
            (changes.connections?.create?.length || 0) +
            (changes.connections?.update?.length || 0) +
            (changes.connections?.delete?.length || 0) +
            (changes.streetLights?.create?.length || 0) +
            (changes.streetLights?.update?.length || 0) +
            (changes.streetLights?.delete?.length || 0);
        
        // Initialiser la progression
        setSaveProgress({
            current: 0,
            total: totalOperations,
            currentStep: 'Préparation de la sauvegarde...',
            percentage: 0
        });
        
        const allErrors: GridEditorError[] = [];
        let hasPartialSuccess = false;
        let currentOperation = 0;

        try {
            // Map pour stocker les correspondances entre les IDs temporaires et les IDs réels des nodes créés
            const nodeIdMap = new Map<string, string>();

            // Création des nodes (éléments à créer)
            if (changes.elements?.create?.length) {
                setSaveProgress(prev => ({
                    ...prev,
                    currentStep: `Création des éléments (${changes.elements.create.length})...`
                }));
                
                const createResults = await Promise.allSettled(
                    changes.elements.create.map(async (node, index) => {
                        try {
                            const nodeData = convertGridElementToNodePlan(node, node.type);
                            if (!nodeData) {
                                throw new Error(`Impossible de convertir l'élément ${node.id} en NodePlan`);
                            }
                            
                            const createdNode = await retryOperation(
                                () => planBTAPI.createNode(planBTIdFound as string, nodeData),
                                3,
                                1000
                            );
                            
                            // Stocker la correspondance entre l'ID temporaire et l'ID réel
                            if (createdNode.node_id) {
                                nodeIdMap.set(node.id, createdNode.node_id);
                            }
                            
                            // Mettre à jour la progression
                            currentOperation++;
                            setSaveProgress(prev => ({
                                ...prev,
                                current: currentOperation,
                                percentage: Math.round((currentOperation / totalOperations) * 100),
                                currentStep: `Création des éléments (${index + 1}/${changes.elements.create.length})...`
                            }));
                            
                            return createdNode;
                        } catch (err) {
                            const error = analyzeAxiosError(err, 'create', node.id);
                            allErrors.push(error);
                            throw error;
                        }
                    })
                );

                const createErrors = createResults
                    .filter((result): result is PromiseRejectedResult => result.status === 'rejected')
                    .map(result => result.reason as GridEditorError);
                
                if (createErrors.length > 0) {
                    allErrors.push(...createErrors);
                    hasPartialSuccess = createErrors.length < changes.elements.create.length;
                }
            }

            // Suppression des nodes (éléments à supprimer)
            if (changes.elements?.delete?.length) {
                setSaveProgress(prev => ({
                    ...prev,
                    currentStep: `Suppression des éléments (${changes.elements.delete.length})...`
                }));
                
                const deleteResults = await Promise.allSettled(
                    changes.elements.delete.map(async (node, index) => {
                        try {
                            const result = await retryOperation(
                                () => planBTAPI.deleteNode(planBTIdFound as string, node.id),
                                3,
                                1000
                            );
                            
                            // Mettre à jour la progression
                            currentOperation++;
                            setSaveProgress(prev => ({
                                ...prev,
                                current: currentOperation,
                                percentage: Math.round((currentOperation / totalOperations) * 100),
                                currentStep: `Suppression des éléments (${index + 1}/${changes.elements.delete.length})...`
                            }));
                            
                            return result;
                        } catch (err) {
                            const error = analyzeAxiosError(err, 'delete', node.id);
                            allErrors.push(error);
                            throw error;
                        }
                    })
                );

                const deleteErrors = deleteResults
                    .filter((result): result is PromiseRejectedResult => result.status === 'rejected')
                    .map(result => result.reason as GridEditorError);
                
                if (deleteErrors.length > 0) {
                    allErrors.push(...deleteErrors);
                    hasPartialSuccess = deleteErrors.length < changes.elements.delete.length;
                }
            }

            // Mise à jour des nodes (éléments à modifier)
            if (changes.elements?.update?.length) {
                setSaveProgress(prev => ({
                    ...prev,
                    currentStep: `Mise à jour des éléments (${changes.elements.update.length})...`
                }));
                
                const updateResults = await Promise.allSettled(
                    changes.elements.update.map(async (node, index) => {
                        try {
                            const nodeData = convertGridElementToNodePlan(node, node.type);
                            if (!nodeData) {
                                throw new Error(`Impossible de convertir l'élément ${node.id} en NodePlan`);
                            }
                            
                            const result = await retryOperation(
                                () => planBTAPI.updateNode(planBTIdFound as string, node.id, nodeData),
                                3,
                                1000
                            );
                            
                            // Mettre à jour la progression
                            currentOperation++;
                            setSaveProgress(prev => ({
                                ...prev,
                                current: currentOperation,
                                percentage: Math.round((currentOperation / totalOperations) * 100),
                                currentStep: `Mise à jour des éléments (${index + 1}/${changes.elements.update.length})...`
                            }));
                            
                            return result;
                        } catch (err) {
                            const error = analyzeAxiosError(err, 'update', node.id);
                            allErrors.push(error);
                            throw error;
                        }
                    })
                );

                const updateErrors = updateResults
                    .filter((result): result is PromiseRejectedResult => result.status === 'rejected')
                    .map(result => result.reason as GridEditorError);

                if (updateErrors.length > 0) {
                    hasPartialSuccess = updateErrors.length < changes.elements.update.length;
                }
            }

            // Gestion des connexions - Création des liens
            if (changes.connections?.create?.length) {
                const createLinkResults = await Promise.allSettled(
                    changes.connections.create.map(async (connection) => {
                        try {
                            const linkData = convertConnectionToLinkPlan(connection, nodeIdMap, planBTIdFound as string);
                            
                            return await retryOperation(
                                () => planBTAPI.createLink(planBTIdFound as string, linkData),
                                3,
                                1000
                            );
                        } catch (err) {
                            const error = analyzeAxiosError(err, 'create', connection.id);
                            allErrors.push(error);
                            throw error;
                        }
                    })
                );

                const createLinkErrors = createLinkResults
                    .filter((result): result is PromiseRejectedResult => result.status === 'rejected')
                    .map(result => result.reason as GridEditorError);
                
                if (createLinkErrors.length > 0) {
                    allErrors.push(...createLinkErrors);
                    hasPartialSuccess = createLinkErrors.length < changes.connections.create.length;
                }
            }

            // Gestion des connexions - Mise à jour des liens
            if (changes.connections?.update?.length) {
                const updateLinkResults = await Promise.allSettled(
                    changes.connections.update.map(async (connection) => {
                        try {
                            const linkData = convertConnectionToLinkPlan(connection, nodeIdMap, planBTIdFound as string);
                            
                            return await retryOperation(
                                () => planBTAPI.updateLink(planBTIdFound as string, connection.id, linkData),
                                3,
                                1000
                            );
                        } catch (err) {
                            const error = analyzeAxiosError(err, 'update', connection.id);
                            allErrors.push(error);
                            throw error;
                        }
                    })
                );

                const updateLinkErrors = updateLinkResults
                    .filter((result): result is PromiseRejectedResult => result.status === 'rejected')
                    .map(result => result.reason as GridEditorError);
                
                if (updateLinkErrors.length > 0) {
                    allErrors.push(...updateLinkErrors);
                    hasPartialSuccess = updateLinkErrors.length < changes.connections.update.length;
                }
            }

            // Gestion des connexions - Suppression des liens
            if (changes.connections?.delete?.length) {
                const deleteLinkResults = await Promise.allSettled(
                    changes.connections.delete.map(async (connection) => {
                        try {
                            return await retryOperation(
                                () => planBTAPI.deleteLink(planBTIdFound as string, connection.id),
                                3,
                                1000
                            );
                        } catch (err) {
                            const error = analyzeAxiosError(err, 'delete', connection.id);
                            allErrors.push(error);
                            throw error;
                        }
                    })
                );

                const deleteLinkErrors = deleteLinkResults
                    .filter((result): result is PromiseRejectedResult => result.status === 'rejected')
                    .map(result => result.reason as GridEditorError);
                
                if (deleteLinkErrors.length > 0) {
                    allErrors.push(...deleteLinkErrors);
                    hasPartialSuccess = deleteLinkErrors.length < changes.connections.delete.length;
                }
            }

            // Gestion des street lights - Création
            if (changes.streetLights?.create?.length) {
                console.log('💡 Creating street lights:', changes.streetLights.create);
                const createLightResults = await Promise.allSettled(
                    changes.streetLights.create.map(async (light) => {
                        try {
                            const streetLightData: StreetLightPatch = {
                                str_light_name: light.name || 'Street Light',
                                str_light_pole: light.poleId || '',
                                str_light_lat: 0, // Will be set from pole coordinates
                                str_light_lng: 0, // Will be set from pole coordinates
                                str_light_number: light.number || light.id,
                                str_light_account_id: '', // Will be set from plan context
                                str_light_zhaga_devui: light.zhagaDevui || ''
                            };
                            
                            console.log('💡 Creating street light with data:', streetLightData);
                            return await retryOperation(
                                () => streetLightAPI.createNewStreetLight(streetLightData),
                                3,
                                1000
                            );
                        } catch (err) {
                            const error = analyzeAxiosError(err, 'create', light.id);
                            allErrors.push(error);
                            throw error;
                        }
                    })
                );

                const createLightErrors = createLightResults
                    .filter((result): result is PromiseRejectedResult => result.status === 'rejected')
                    .map(result => result.reason as GridEditorError);
                
                if (createLightErrors.length > 0) {
                    allErrors.push(...createLightErrors);
                    hasPartialSuccess = createLightErrors.length < changes.streetLights.create.length;
                }
            }

            // Gestion des street lights - Mise à jour
            if (changes.streetLights?.update?.length) {
                console.log('💡 Updating street lights:', changes.streetLights.update);
                const updateLightResults = await Promise.allSettled(
                    changes.streetLights.update.map(async (light) => {
                        try {
                            const streetLightData: StreetLightPatch = {
                                str_light_name: light.name || 'Street Light',
                                str_light_pole: light.poleId || '',
                                str_light_lat: 0, // Will be set from pole coordinates
                                str_light_lng: 0, // Will be set from pole coordinates
                                str_light_number: light.number || light.id,
                                str_light_account_id: '', // Will be set from plan context
                                str_light_zhaga_devui: light.zhagaDevui || ''
                            };
                            
                            console.log('💡 Updating street light with data:', streetLightData);
                            return await retryOperation(
                                () => streetLightAPI.updateStreetLight(light.id, streetLightData),
                                3,
                                1000
                            );
                        } catch (err) {
                            const error = analyzeAxiosError(err, 'update', light.id);
                            allErrors.push(error);
                            throw error;
                        }
                    })
                );

                const updateLightErrors = updateLightResults
                    .filter((result): result is PromiseRejectedResult => result.status === 'rejected')
                    .map(result => result.reason as GridEditorError);
                
                if (updateLightErrors.length > 0) {
                    allErrors.push(...updateLightErrors);
                    hasPartialSuccess = updateLightErrors.length < changes.streetLights.update.length;
                }
            }

            // Gestion des street lights - Suppression
            if (changes.streetLights?.delete?.length) {
                console.log('💡 Deleting street lights:', changes.streetLights.delete);
                const deleteLightResults = await Promise.allSettled(
                    changes.streetLights.delete.map(async (light) => {
                        try {
                            console.log('💡 Deleting street light:', light.id);
                            return await retryOperation(
                                () => streetLightAPI.deleteStreetLight(light.id),
                                3,
                                1000
                            );
                        } catch (err) {
                            const error = analyzeAxiosError(err, 'delete', light.id);
                            allErrors.push(error);
                            throw error;
                        }
                    })
                );

                const deleteLightErrors = deleteLightResults
                    .filter((result): result is PromiseRejectedResult => result.status === 'rejected')
                    .map(result => result.reason as GridEditorError);
                
                if (deleteLightErrors.length > 0) {
                    allErrors.push(...deleteLightErrors);
                    hasPartialSuccess = deleteLightErrors.length < changes.streetLights.delete.length;
                }
            }

            setErrors(allErrors);

            // Log du résultat final
            const result = allErrors.length === 0 ? {
                success: true,
                message: "Toutes les modifications ont été sauvegardées avec succès"
            } : hasPartialSuccess ? {
                success: false,
                partialSuccess: true,
                errors: allErrors,
                message: "Certaines modifications ont été sauvegardées avec succès, mais des erreurs sont survenues"
            } : {
                success: false,
                errors: allErrors,
                message: "Aucune modification n'a pu être sauvegardée"
            };

            console.log('🏁 FIN DE LA SAUVEGARDE - Résultat:', {
                '✅ Succès': result.success,
                '⚠️ Succès partiel': result.partialSuccess || false,
                '❌ Erreurs': allErrors.length,
                '📝 Message': result.message,
                '🔍 Détails des erreurs': allErrors.map(err => ({
                    type: err.type,
                    message: err.message,
                    elementId: err.elementId,
                    operation: err.operation
                }))
            });

            const nodeIdMapRecord = Object.fromEntries(nodeIdMap);

            if (allErrors.length === 0) {
                console.log("✅ Toutes les modifications ont été sauvegardées avec succès");
                return {
                    success: true,
                    message: "Toutes les modifications ont été sauvegardées avec succès",
                    nodeIdMap: nodeIdMapRecord
                };
            } else if (hasPartialSuccess) {
                console.warn("⚠️ Certaines modifications ont été sauvegardées avec succès");
                return {
                    success: false,
                    partialSuccess: true,
                    errors: allErrors,
                    message: "Certaines modifications ont été sauvegardées avec succès, mais des erreurs sont survenues",
                    nodeIdMap: nodeIdMapRecord
                };
            } else {
                console.error("❌ Aucune modification n'a pu être sauvegardée");
                return {
                    success: false,
                    errors: allErrors,
                    message: "Aucune modification n'a pu être sauvegardée",
                    nodeIdMap: nodeIdMapRecord
                };
            }

        } catch (error) {
            const unknownError: GridEditorError = {
                type: GridEditorErrorType.UNKNOWN_ERROR,
                message: `Erreur inattendue: ${error instanceof Error ? error.message : 'Erreur inconnue'}`,
                details: error,
                retryable: false
            };
            
            setErrors([unknownError]);
            console.error("❌ Erreur inattendue lors de la sauvegarde:", error);
            
            return {
                success: false,
                errors: [unknownError],
                message: "Une erreur inattendue s'est produite"
            };
        } finally {
            setIsLoading(false);
            // Réinitialiser la progression
            setSaveProgress({
                current: 0,
                total: 0,
                currentStep: '',
                percentage: 0
            });
        }
    };

    const clearErrors = () => {
        setErrors([]);
    };

    return {
        planBTId: planBTIdFound || "",
        saveChanges,
        isLoading,
        errors,
        clearErrors,
        saveProgress,
    };
};

const convertGridElementToNodePlan = (gridElement: GridElement, nodeType: GridElement['type']): Partial<NodePlan> | undefined => {
    try {
        /*
        // Validation des données requises
        if (!gridElement.id || !gridElement.name) {
            throw new Error(`Élément invalide: ID ou nom manquant pour l'élément ${gridElement.id || 'inconnu'}`);
        }*/
 
        // Validation des coordonnées GPS si présentes
        if (gridElement.gpsCoordinates && 
            (typeof gridElement.gpsCoordinates.latitude !== 'number' || 
             typeof gridElement.gpsCoordinates.longitude !== 'number')) {
            throw new Error(`Coordonnées GPS invalides pour l'élément ${gridElement.id}`);
        }

        switch (nodeType) {
            case "pole": {
                const pole: Partial<Pole> = {
                    pole_name: gridElement.name,
                    pole_location_lat: gridElement.gpsCoordinates?.latitude,
                    pole_location_lng: gridElement.gpsCoordinates?.longitude,
                    // Libellé complet (ex "Poteau 42"), jamais le numéro nu. Le
                    // rapprochement compteur->poteau extrait le numéro de façon tolérante
                    // des deux côtés (backend poleNumberOf, front resolvePoleByNumber),
                    // donc "Poteau 42" rapproche bien le numéro de raccordement 42.
                    pole_number: String(gridElement.name ?? gridElement.number ?? ''),
                    pole_type: gridElement.properties?.pole_type || 'WOOD',
                    pole_id: gridElement.hostId,
                    pole_status: gridElement.status
                };

                return {
                    node_id: gridElement.id,
                    node_host_name: gridElement.name,
                    node_location_lat: gridElement.gpsCoordinates?.latitude,
                    node_location_lng: gridElement.gpsCoordinates?.longitude,
                    node_type: "POLE",
                    node_host: pole
                };
            }

            case "transformer": {
                const transformer: Partial<Transformer> = {
                    trnsf_name: gridElement.name,
                    trnsf_latitude: gridElement.gpsCoordinates?.latitude,
                    trnsf_longitude: gridElement.gpsCoordinates?.longitude,
                    trnsf_number: gridElement.name,
                    trnsf_id: gridElement.hostId,
                    trnsf_status: gridElement.status
                };

                return {
                    node_id: gridElement.id,
                    node_host_name: gridElement.name,
                    node_location_lat: gridElement.gpsCoordinates?.latitude,
                    node_location_lng: gridElement.gpsCoordinates?.longitude,
                    node_type: "TRANSFORMER",
                    node_host: transformer
                };
            }

            case "meter": {
                const meter: Partial<Meter> = {
                    mtr_name: gridElement.name,
                    mtr_latitude: gridElement.gpsCoordinates?.latitude,
                    mtr_longitude: gridElement.gpsCoordinates?.longitude,
                    mtr_number: String(gridElement.properties?.mtr_number || gridElement.number || ''),
                    mtr_id: gridElement.hostId,
                    mtr_status: gridElement.status,
                    mtr_box_asso_id: gridElement.boxAssoId,
                    mtr_is_jouly: gridElement.isJouly,
                };

                return {
                    node_id: gridElement.id,
                    node_host_name: gridElement.name,
                    node_location_lat: gridElement.gpsCoordinates?.latitude,
                    node_location_lng: gridElement.gpsCoordinates?.longitude,
                    node_type: "METER",
                    node_host: meter
                };
            }

            case "power-plant": {
                const centralPlant: Partial<CentralPlant> = {
                    cntrlp_name: gridElement.name,
                    cntrlp_latitude: gridElement.gpsCoordinates?.latitude,
                    cntrlp_longitude: gridElement.gpsCoordinates?.longitude,
                    cntrlp_number: gridElement.name,
                    cntrlp_id: gridElement.hostId,
                    cntrlp_status: gridElement.status
                };

                return {
                    node_id: gridElement.id,
                    node_host_name: gridElement.name,
                    node_location_lat: gridElement.gpsCoordinates?.latitude,
                    node_location_lng: gridElement.gpsCoordinates?.longitude,
                    node_type: "CENTRALPLANT",
                    node_host: centralPlant
                };
            }

            default:
                console.warn(`Type de node non supporté: ${nodeType} pour l'élément ${gridElement.id}`);
                return undefined;
        }

    } catch (error) {
        console.error(`Erreur lors de la conversion de l'élément ${gridElement.id}:`, error);
        throw error;
    }
};

// Fonction pour convertir une connexion en LinkPlan
const convertConnectionToLinkPlan = (
    connection: Connection, 
    nodeIdMap: Map<string, string>, 
    planBTId: string
): Partial<LinkPlan> => {
    try {
        // Récupérer les IDs des nodes depuis la map (pour les nouveaux nodes)
        // ou utiliser directement les IDs si les nodes existent déjà
        const node1Id = nodeIdMap.get(connection.fromId) || connection.fromId;
        const node2Id = nodeIdMap.get(connection.toId) || connection.toId;

        if (!node1Id || !node2Id) {
            throw new Error(`Impossible de trouver les IDs des nodes pour la connexion ${connection.id}. FromId: ${connection.fromId}, ToId: ${connection.toId}`);
        }

        return {
            pl_link_id: connection.id,
            pl_link_name: connection.name || connection.id, // Utiliser le nom de la connexion ou l'ID comme fallback
            pl_link_node_1: node1Id,
            pl_link_node_2: node2Id,
            pl_link_plan: planBTId,
            pl_link_status: connection.status, // Utiliser le statut de la connexion
            // Les autres champs seront remplis par le serveur
        };
    } catch (error) {
        console.error(`Erreur lors de la conversion de la connexion ${connection.id}:`, error);
        throw error;
    }
};

export default useGridEditorForPlanBT;